// Speed test over the admin WebSocket (middleware 0.7.18, websocket-speed-test-
// endpoint.md). The command answers at once with an ack or an error frame; the
// TV's result arrives later as five `speed_test_*` info fields of the room,
// announced only by a `room_info` event that names the project and the room.
// That event is throttled (one per project per second, the rest DROPPED) and
// the server sets no timeout, so the console keeps its own and reads the room
// once more when it fires.

import type { HotelExecutives } from '@/features/projects/projectsSlice';

// The five info fields the server writes; every value is a string.
export const SPEED_TEST_FIELDS = {
  download:		'speed_test_download',
  upload:		'speed_test_upload',
  latency:		'speed_test_latency',
  status:		'speed_test_status',
  timestamp:	'speed_test_timestamp',
} as const;

// No answer from the TV after this long: read the room one last time, then
// give up. A speed test usually takes 10–30 s on the TV.
export const SPEED_TEST_TIMEOUT_MS = 90_000;

// Error codes a speed_test command can be answered with (plus invalid_json,
// which any malformed frame gets).
export const SPEED_TEST_ERROR_CODES = [
  'bad_speed_test_target',
  'user_lookup_failed',
  'project_not_found',
  'not_authorized',
  'device_offline',
  'invalid_json',
] as const;

// Client-side codes: never sent by the server.
export const SPEED_TEST_NOT_CONNECTED = 'ws_not_connected';
export const SPEED_TEST_NO_RESULT = 'no_result';

export interface SpeedTestResult {
  download:		string;
  upload:		string;
  latency:		string;
  status:		string;
  // Server time (Unix seconds) the result was stored; 0 when absent.
  timestamp:	number;
}

interface FieldLike {
  type:	string;
  url?:	string;
}

function fieldValue(fields: FieldLike[], type: string): string {
  const f = fields.find((x) => x && x.type === type);
  return f && typeof f.url === 'string' ? f.url : '';
}

// GET /rooms.json?id=&room_no=&info=1 answers with the info record (`fields`
// at the top); without info=1 it is the room, with them under `info.fields`.
// Accept either so the reader does not depend on which one was asked for.
export function extractInfoFields(result: unknown): FieldLike[] {
  if (!result || typeof result !== 'object')
    return [];

  const r = result as { fields?: unknown; info?: { fields?: unknown } };
  if (r.info && Array.isArray(r.info.fields))
    return r.info.fields as FieldLike[];
  if (Array.isArray(r.fields))
    return r.fields as FieldLike[];
  return [];
}

export function readSpeedTestResult(fields: FieldLike[] | undefined | null): SpeedTestResult | null {
  if (!fields || fields.length === 0)
    return null;

  const ts = fieldValue(fields, SPEED_TEST_FIELDS.timestamp);
  const status = fieldValue(fields, SPEED_TEST_FIELDS.status);
  if (!ts && !status)
    return null;

  const parsed = parseInt(ts, 10);
  return {
    download:	fieldValue(fields, SPEED_TEST_FIELDS.download),
    upload:		fieldValue(fields, SPEED_TEST_FIELDS.upload),
    latency:	fieldValue(fields, SPEED_TEST_FIELDS.latency),
    status,
    timestamp:	isNaN(parsed) ? 0 : parsed,
  };
}

// A stored timestamp equal to or later than the ack's marks the new result.
// Both are the server's clock, so no skew between them.
export function isNewSpeedTestResult(result: SpeedTestResult | null, ackTimestamp: number | undefined): boolean {
  if (!result || !result.timestamp || !ackTimestamp)
    return false;
  return result.timestamp >= ackTimestamp;
}

export interface SpeedTestCaller {
  isAdmin:			boolean;
  username?:		string;
  localProjectId?:	string;
  hasSeeAll?:		boolean;
}

// Who may START a test — the POST /tv_control.json rule (Authorization §):
// admins; a local user of this very project; anyone on its partner,
// chain_manager or manager list unless they are also on mw_light. Being the
// owner does not count, nor does seeing the project only through
// SeeAllProjects. A chain manager of the project's GROUP is not visible to
// the console; such a user reaches the project without SeeAllProjects, so a
// non-SeeAll user who can open the page is let through and the server decides.
export function canStartSpeedTest(caller: SpeedTestCaller, projectId: string, executives?: HotelExecutives | null): boolean {
  if (caller.isAdmin)
    return true;

  const name = caller.username;
  if (!name || !projectId)
    return false;

  if (caller.localProjectId)
    return caller.localProjectId === projectId;

  if (executives?.mw_light?.includes(name))
    return false;

  const listed =	!!executives &&
					(	executives.partner?.includes(name) ||
						executives.chain_manager?.includes(name) ||
						executives.manager?.includes(name)	);
  if (listed)
    return true;

  return !caller.hasSeeAll;
}

export function speedTestErrorKey(code: string | undefined): string {
  switch (code) {
    case 'bad_speed_test_target':
    case 'user_lookup_failed':
    case 'project_not_found':
    case 'not_authorized':
    case 'device_offline':
    case 'invalid_json':
    case SPEED_TEST_NOT_CONNECTED:
    case SPEED_TEST_NO_RESULT:
      return 'projects:project:rooms:info:speed_test_errors:' + code;
    default:
      return 'projects:project:rooms:info:speed_test_errors:unknown';
  }
}
